import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Analytics } from '../../../../services/analytics';
import { applicationDataSavedEvent, saveAndContinueEvent } from '../../../../services/analytics/analyticsEvents';
import { sendEmail } from '../../../../services/api/sendEmail/sendEmail';
import { updateDataStorageNew } from '../../../../services/api/updateDataStorage/updateDataStorage';
import { getTransactionId } from '../../../../services/common/data';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { selectSaveAndContinueData } from '../../../../services/redux/features/dataStorage.redux';
import { loadingActions, loadingSelectors } from '../../../../services/redux/features/loading.slice';
// eslint-disable-next-line
import { ContinueLater, ContinueLaterFloatingButton } from '@vwfs-its/sf-sac-frontend';

interface SaveAndContinueModalButtonProps {
  isHybridJourney?: boolean;
}

interface SaveAndContinueInitialValues {
  email?: string;
  phoneCountryCode: string;
  phoneNumber: string;
}

export const SaveAndContinueModal: React.FC<SaveAndContinueModalButtonProps> = ({ isHybridJourney = false }) => {
  const [sncModalOpen, setSncModalOpen] = useState(false);
  const [initialValues, setInitialValues] = useState<SaveAndContinueInitialValues>({
    email: undefined,
    phoneCountryCode: '+49',
    phoneNumber: '',
  });
  const handleOpenModal = () => setSncModalOpen(true);
  const handleCloseModal = () => setSncModalOpen(false);
  const isLoading = useSelector(loadingSelectors.selectLoading);
  const creditApplicantCustomerData = useSelector(
    creditApplicationSelectors.selectCreditApplicantCustomerData
  ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;
  const saveAndContinueData = useSelector(selectSaveAndContinueData);
  const dispatch = useDispatch();

  useEffect(() => {
    setInitialValues({
      email: saveAndContinueData.email ?? creditApplicantCustomerData?.contactData?.emails?.[0]?.emailAddress,
      phoneCountryCode:
        saveAndContinueData.phoneCountryCode ??
        creditApplicantCustomerData?.contactData?.mobilePhoneCountryCode ??
        '+49',
      phoneNumber: saveAndContinueData.phoneNumber ?? creditApplicantCustomerData?.contactData?.mobilePhoneNumber ?? '',
    });
  }, [creditApplicantCustomerData, saveAndContinueData]);

  const onSnCSubmit = async (values, modalState, setShowMailDispatched) => {
    if (values.continueLaterDataProtection) {
      Analytics.push(applicationDataSavedEvent);
    }

    dispatch(loadingActions.setLoading(true));

    const dataStoragePayload = {
      custom: {
        saveAndContinueMobilePhoneNumber: values.continueLaterPhone,
        saveAndContinueMobilePhoneCountryCode: values.continueLaterPhoneCountryCode,
        saveAndContinueEmail: values.continueLaterEmail,
      },
    };
    const transactionId = getTransactionId();
    await updateDataStorageNew(transactionId, dataStoragePayload);

    await sendEmail().finally(() => {
      dispatch(loadingActions.setLoading(false));
      if (modalState && setShowMailDispatched) {
        modalState.setShowContinueLater(false);
        setShowMailDispatched(true);
      }
    });
    Analytics.push(saveAndContinueEvent(creditApplicantCustomerData));
  };

  if (isHybridJourney) {
    return null;
  }

  return (
    <>
      <div id="float-grid" className="o-float-grid">
        <div className="o-float-grid__area" data-float-grid-area="bottom-right">
          <ContinueLaterFloatingButton version={1} onClick={handleOpenModal} />
        </div>
      </div>
      <ContinueLater
        version={1}
        productName="posfr-fe" // See feature adaption definition, as this is a variable in Content Sheet.
        email={initialValues.email}
        phoneCountryCode={initialValues.phoneCountryCode}
        phoneNo={initialValues.phoneNumber}
        dataProtection={false}
        testId="sncModal"
        showModal={{
          showContinueLater: sncModalOpen && !isLoading,
          setShowContinueLater: setSncModalOpen,
        }}
        onCancel={() => {
          handleCloseModal();
        }}
        dataModels={{}}
        onContinueLaterSubmit={onSnCSubmit}
        onSendAgainSubmit={onSnCSubmit}
      />
    </>
  );
};
